import { Injectable, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, forkJoin, map, merge, of, pairwise, partition, Subject, switchMap, takeUntil, tap } from 'rxjs';

@Injectable()
export class CustomTitleStrategy extends TitleStrategy implements OnDestroy {
  private disposed$: Subject<void> = new Subject();
  private latestRouterState: RouterStateSnapshot | null = null;

  constructor(
    private readonly title: Title,
    private readonly translateService: TranslateService
  ) {
    super();
    this.translateService.onLangChange
      .pipe(
        takeUntil(this.disposed$),
        pairwise(),
        filter(() => !!this.latestRouterState),
        tap(() =>
          this.updateTitle(this.latestRouterState as RouterStateSnapshot)
        )
      )
      .subscribe();
  }

  override updateTitle(routerState: RouterStateSnapshot) {
    this.latestRouterState = routerState;
    const pageTtitle = this.buildTitle(routerState);
    const [customTitle$, defaultTitle$] = partition(
      of(pageTtitle),
      currentPageTitle => currentPageTitle !== undefined
    );
    merge(
      customTitle$.pipe(
        map(customTitle => customTitle as string),
        switchMap(customTitle =>
          forkJoin([
            this.translateService.get(`titles.${customTitle}`),
            this.translateService.get('titles.default'),
          ])
        ),
        map(
          ([customTitleTranslated, defaultTitle]) =>
            `${customTitleTranslated} - ${defaultTitle}`
        )
      ),
      defaultTitle$.pipe(
        switchMap(() => this.translateService.get('titles.default'))
      )
    )
      .pipe(tap(title => this.title.setTitle(title)))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.disposed$.next();
    this.disposed$.complete();
  }
}
